/**
 * The shape of a transcribed scorecard, and the two helpers that read one.
 *
 * Separate from seed.ts only to break a cycle: a course file needs `tees()` and
 * the `Seed` shape, and seed.ts needs the courses. Importing each other left
 * `tees` undefined at module-init time — the kind of failure that shows up as
 * "tees is not a function" a long way from its cause.
 */

import type { Hole, TeeSet } from '../engine/types'

/*
 * Tee columns the cards supply, longest first.
 *
 * "Bronze" was a phantom. No Bandon card has one: every card prints Black,
 * Green, Gold and Royal Blue in a block, with Orange on its own row further
 * down. The old fourth column held Orange's yardages under Bronze's name and a
 * rating belonging to neither, and Royal Blue — the shortest tee, which a
 * short-hitter might actually want — was missing entirely.
 *
 * Ordered by total yardage rather than by position on the card, because Orange
 * plays longer than Royal Blue and the invariant tests read this order.
 */
const YARD_TEES = ['black', 'green', 'gold', 'orange', 'royalblue'] as const

/**
 * Build holes from a transcribed card.
 *
 * `null` in a yardage column means the source was garbled for that one cell and
 * nothing was invented to fill it — a missing yardage is cosmetic, an invented
 * one is a lie on a scorecard.
 */
export function cardHoles(pars: number[], si: number[], yards: (number | null)[][]): Hole[] {
  return pars.map((par, i) => {
    const y: Record<string, number> = {}
    YARD_TEES.forEach((tee, t) => {
      const v = yards[t]?.[i]
      if (typeof v === 'number') y[tee] = v
    })
    return { number: i + 1, par, strokeIndex: si[i]!, yards: y }
  })
}

/**
 * A tee's own numbers. The sixth element is its PAR, and it is optional only
 * because most tees share the course's.
 *
 * `courseHandicap18` is `index x slope/113 + (rating - par)` and that par is the
 * TEE'S. Black, Green and Gold at these courses genuinely play the course par, so
 * omitting it is correct for them. A family tee does not — Royal Blue is under
 * 4,000 yards over eighteen and is not a par 72 — so one added without its own par
 * moves every course handicap on it by several strokes. Supply it, or leave the tee
 * out until somebody has read the card.
 */
export const tees = (
  specs: [string, string, number, number, number, number?][],
): TeeSet[] =>
  specs.map(([id, name, rating, slope, yards, par]) => ({
    id,
    name,
    color:
      id === 'black'
        ? '#0A0A0A'
        : id === 'green'
          ? '#1F5A3D'
          : id === 'gold'
            ? '#B08D33'
            : id === 'orange'
              ? '#C4622D'
              : '#2B4C8C',
    rating,
    slope,
    par: par ?? 0,
    totalYards: yards,
  }))

export interface Seed {
  id: string
  name: string
  par: number
  pars: number[]
  /** Stroke index per hole, 1 = hardest. Transcribed, never generated. */
  si: number[]
  /** Yardages in YARD_TEES order; null where the source cell was garbled. */
  yards: (number | null)[][]
  tees: TeeSet[]
  /*
   * True only when a human has matched this card to the physical one.
   *
   * Per seed rather than blanket, because it is now a mixed set: the five Bandon
   * cards were photographed at the course, the Idaho ones are still transcribed
   * from databases and must keep saying so.
   */
  verified?: boolean
  /** Where these numbers came from and how far to trust them. */
  provenance: string
}

